import * as React from 'react';
import {useState} from 'react';
import {KatButton} from '@amzn/katal-react';
import {CaseHistory, HistoryEntry, AttachmentEntry} from 'src/app/model/lighthouse/Common';
import TabContainer, {Tab} from 'src/app/component/lighthouse/review/TabContainer';

export interface CaseHistorySectionProps {
    /** Combined history data: audit trail + file attachments. */
    history: CaseHistory;
    /** Called with the attachment's fileId when its "View" button is clicked. Parent triggers the download. */
    onViewAttachment?: (fileId: string) => void;
}

/**
 * Displays case history: audit trail entries with expandable notes,
 * and file attachments with a View action.
 */
/** Max characters shown inline before collapsing to "View more" (~one line in the history column). */
const MAX_PREVIEW_CHARS = 80;
/** Max lines shown inline before collapsing to "View more" (keeps rows compact for multi-line notes). */
const MAX_PREVIEW_LINES = 2;

/**
 * Preview shown inline in the trail row. Collapses to "View more" when the note is too long
 * (> MAX_PREVIEW_CHARS) OR spans too many lines (> MAX_PREVIEW_LINES). Full text stays in the popup.
 */
const getNotePreview = (text: string): { preview: string; isTruncated: boolean } => {
    if (!text) return {preview: '', isTruncated: false};
    const tooLong = text.length > MAX_PREVIEW_CHARS;
    const lines = text.split(/\r?\n/);
    const tooManyLines = lines.length > MAX_PREVIEW_LINES;
    if (!tooLong && !tooManyLines) return {preview: text, isTruncated: false};
    // Char limit wins; otherwise collapse the multi-line note down to its first line.
    const base = tooLong ? text.slice(0, MAX_PREVIEW_CHARS) : lines[0];
    return {preview: base.trimEnd() + '...', isTruncated: true};
};

const formatTimestamp = (timestamp: number): string => {
    return new Date(timestamp).toLocaleString('en-US');
};

/** Drops the trailing ` with comment [...]` clause (the comment is shown in the popup). */
const stripCommentClause = (auditLog: string): string =>
    auditLog.replace(/\s*with comment \[[\s\S]*\]\s*$/, '');

/** Cleans the audit sentence for the row body: drops the leading <timestamp>/[alias] and
 *  trailing " by <alias>" (both shown as header chips) plus the comment clause. */
const cleanAuditLogBody = (auditLog: string): string => {
    let body = stripCommentClause(auditLog).trim();
    body = body.replace(/^<[^>]*>\s*/, '');
    body = body.replace(/^\[[^\]]*\]\s*/, '');
    body = body.replace(/\s+by\s+\S+\s*$/, '');
    return body.trim();
};

const CaseHistorySection: React.FC<CaseHistorySectionProps> = ({history, onViewAttachment}) => {
    const [expandedEntry, setExpandedEntry] = useState<HistoryEntry | null>(null);

    // Show newest first. Copy before sorting so we never mutate the prop arrays.
    const sortedAuditTrail = [...history.auditTrail].sort((a, b) => b.timestamp - a.timestamp);
    const sortedAttachments = [...history.attachments].sort((a, b) => b.uploadedAt - a.uploadedAt);

    /** An entry has expandable detail if its remark is truncated or it carries audit-check responses. */
    const hasDetail = (entry: HistoryEntry, isTruncated: boolean): boolean =>
        isTruncated || Boolean(entry.auditCheckResponses && Object.keys(entry.auditCheckResponses).length > 0);

    const auditTrailTab: React.ReactNode = (
        <div className="lighthouse-history-trail" data-testid="audit-trail">
            {sortedAuditTrail.length === 0 ? (
                <span className="lighthouse-empty-state" data-testid="audit-trail-empty">
                    No history available
                </span>
            ) : (
                <div className="lighthouse-trail-list">
                    {sortedAuditTrail.map((entry: HistoryEntry, index: number) => {
                        const {preview, isTruncated} = getNotePreview(entry.notes);
                        // auditLog rows show the cleaned sentence; COMMENT/optimistic rows use the
                        // "Remark:" block. COMMENT is excluded so it renders the same before and after
                        // reload (its backend auditLog is a noisy "added a comment: [...]" sentence).
                        const hasAuditLog = entry.action !== 'COMMENT'
                            && Boolean(entry.auditLog && entry.auditLog.trim().length > 0);
                        return (
                            <div key={`trail-${index}`} className="lighthouse-trail-entry" data-testid={`trail-entry-${index}`}>
                                {/* Shared header (auditor / action / timestamp) on every row. */}
                                <div className="lighthouse-trail-header">
                                    <span className="lighthouse-trail-auditor">{entry.auditor}</span>
                                    <span className="lighthouse-trail-action">{entry.action}</span>
                                    <span className="lighthouse-trail-time">{formatTimestamp(entry.timestamp)}</span>
                                </div>
                                {hasAuditLog ? (
                                    <div className="lighthouse-trail-log" data-testid={`trail-log-${index}`}>
                                        {cleanAuditLogBody(entry.auditLog!)}
                                        {/* "View more" reveals the stripped comment / audit checks. */}
                                        {(Boolean(entry.notes)
                                            || Boolean(entry.auditCheckResponses
                                                && Object.keys(entry.auditCheckResponses).length > 0)) && (
                                            <button
                                                className="lighthouse-view-more"
                                                data-testid={`view-more-${index}`}
                                                onClick={() => setExpandedEntry(entry)}
                                            >
                                                View more
                                            </button>
                                        )}
                                    </div>
                                ) : (
                                    <>
                                        {entry.notes && (
                                            <div className="lighthouse-trail-notes" data-testid={`trail-remark-${index}`}>
                                                <span className="lighthouse-trail-remark-label">Remark: </span>
                                                <span>{preview}</span>
                                                {hasDetail(entry, isTruncated) && (
                                                    <button
                                                        className="lighthouse-view-more"
                                                        data-testid={`view-more-${index}`}
                                                        onClick={() => setExpandedEntry(entry)}
                                                    >
                                                        View more
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                        {!entry.notes && hasDetail(entry, isTruncated) && (
                                            <button
                                                className="lighthouse-view-more"
                                                data-testid={`view-more-${index}`}
                                                onClick={() => setExpandedEntry(entry)}
                                            >
                                                View more
                                            </button>
                                        )}
                                    </>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );

    const attachmentsTab: React.ReactNode = (
        <div className="lighthouse-attachments" data-testid="attachments">
            {sortedAttachments.length === 0 ? (
                <span className="lighthouse-empty-state" data-testid="attachments-empty">
                    No attachments
                </span>
            ) : (
                <div className="lighthouse-attachment-list">
                    {sortedAttachments.map((attachment: AttachmentEntry, index: number) => (
                        <div key={`attachment-${index}`} className="lighthouse-attachment-entry" data-testid={`attachment-${index}`}>
                            <div className="lighthouse-attachment-info">
                                <span className="lighthouse-attachment-name">{attachment.fileName}</span>
                                <span className="lighthouse-attachment-meta">
                                    {attachment.uploadedBy} • {formatTimestamp(attachment.uploadedAt)}
                                </span>
                            </div>
                            {/* Downloads via a fresh pre-signed URL; backend forces content-disposition: attachment. */}
                            <KatButton
                                label="Download"
                                variant="link"
                                onClick={() => onViewAttachment?.(attachment.fileId)}
                                data-testid={`view-attachment-${attachment.fileId}`}
                            />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );

    const tabs: Tab[] = [
        {label: 'Audit Trail', content: auditTrailTab},
        {label: 'Attachments', content: attachmentsTab},
    ];

    const expandedResponses = expandedEntry?.auditCheckResponses;

    return (
        <div className="lighthouse-section" data-testid="case-history-section">
            <div className="lighthouse-section-title">Case History</div>
            <div className="lighthouse-section-content">
                <TabContainer tabs={tabs} defaultActiveIndex={0} />
            </div>

            {/* Expanded detail popup: full remark + decoupled audit-check responses. */}
            {expandedEntry && (
                <div className="lighthouse-note-popup-overlay" data-testid="note-popup-overlay" onClick={() => setExpandedEntry(null)}>
                    <div className="lighthouse-note-popup" onClick={(e) => e.stopPropagation()}>
                        <div className="lighthouse-note-popup-header">
                            <span>Details</span>
                            <button
                                className="lighthouse-note-popup-close"
                                data-testid="note-popup-close"
                                onClick={() => setExpandedEntry(null)}
                                aria-label="Close"
                            >
                                ✕
                            </button>
                        </div>
                        <div className="lighthouse-note-popup-body" data-testid="note-popup-body">
                            {expandedEntry.notes && (
                                <div className="lighthouse-popup-remark" data-testid="popup-remark">
                                    <span className="lighthouse-trail-remark-label">Remark: </span>
                                    <span>{expandedEntry.notes}</span>
                                </div>
                            )}
                            {expandedResponses && Object.keys(expandedResponses).length > 0 && (
                                <div className="lighthouse-popup-audit-checks" data-testid="popup-audit-checks">
                                    <div className="lighthouse-trail-audit-checks-label">Audit Checks:</div>
                                    {Object.entries(expandedResponses).map(([label, answer]) => (
                                        <div key={label} className="lighthouse-trail-audit-check-row">
                                            <span className="lighthouse-trail-audit-check-label">{label}: </span>
                                            <span className="lighthouse-trail-audit-check-answer">{answer}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CaseHistorySection;
